import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../auth/AuthContext.js';
import { useToast } from '../hooks/useToast.js';
import { useMetrics } from '../hooks/useMetrics.js';
import type { MetricEntry, RangePreset } from '../api/metricsTypes.js';
import { parseIsoDate } from './body/bmiUtils.js';
import { BmiCard } from './body/BmiCard.js';
import { MetricsChart } from './body/MetricsChart.js';
import { MetricsTable } from './body/MetricsTable.js';
import { MetricEntryDialog } from './body/MetricEntryDialog.js';
import { ConfirmDeleteDialog } from './body/ConfirmDeleteDialog.js';
import { MetricsSkeleton } from './body/MetricsSkeleton.js';

type DialogState =
  | { mode: 'create' }
  | { mode: 'edit'; entry: MetricEntry }
  | null;

export function Body(): ReactElement {
  const { status } = useAuth();
  const { push } = useToast();
  const metrics = useMetrics();
  const [range, setRange] = useState<RangePreset>('3M');
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const [deleteTarget, setDeleteTarget] = useState<MetricEntry | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const heightCm = useMemo<number | null>(() => {
    if (typeof status === 'string') return null;
    if (!status.authenticated) return null;
    return typeof status.height_cm === 'number' ? status.height_cm : null;
  }, [status]);

  const sortedAsc = useMemo<MetricEntry[]>(() => {
    return [...metrics.entries].sort((a, b) => {
      const ta = parseIsoDate(a.measured_at) ?? 0;
      const tb = parseIsoDate(b.measured_at) ?? 0;
      return ta - tb;
    });
  }, [metrics.entries]);

  const latest = sortedAsc.length > 0 ? sortedAsc[sortedAsc.length - 1]! : null;
  const previous =
    sortedAsc.length > 1 ? sortedAsc[sortedAsc.length - 2]! : null;

  function onEdit(entry: MetricEntry): void {
    setDialogState({ mode: 'edit', entry });
  }

  function onAskDelete(entry: MetricEntry): void {
    setDeleteTarget(entry);
  }

  async function onConfirmDelete(): Promise<void> {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await metrics.remove(deleteTarget.id);
      push('success', 'Measurement deleted');
      setDeleteTarget(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete';
      push('error', msg);
    } finally {
      setDeleting(false);
    }
  }

  async function onCreate(req: import('../api/metricsTypes.js').CreateMetricRequest): Promise<MetricEntry> {
    const created = await metrics.create(req);
    push('success', 'Measurement saved');
    return created;
  }

  async function onUpdate(
    id: number,
    req: import('../api/metricsTypes.js').UpdateMetricRequest,
  ): Promise<MetricEntry> {
    const updated = await metrics.update(id, req);
    push('success', 'Measurement saved');
    return updated;
  }

  return (
    <section
      className="mx-auto flex w-full max-w-[430px] flex-col gap-4 p-4 landscape:max-w-[820px]"
      data-testid="body-view"
    >
      <header className="flex items-center justify-between">
        <h1
          className="text-2xl font-semibold"
          style={{ color: 'var(--text-main)' }}
          data-testid="body-title"
        >
          Body
        </h1>
        <button
          type="button"
          onClick={() => setDialogState({ mode: 'create' })}
          className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium"
          style={{
            background: 'var(--accent)',
            color: 'var(--accent-contrast)',
            minHeight: 48,
          }}
          data-testid="body-add-entry"
        >
          <Plus size={16} aria-hidden="true" />
          <span>+ Entry</span>
        </button>
      </header>

      <BmiCard heightCm={heightCm} latest={latest} previous={previous} />

      {metrics.loading ? (
        <MetricsSkeleton />
      ) : (
        <>
          <MetricsChart
            entries={metrics.entries}
            range={range}
            onRangeChange={setRange}
          />

          {metrics.error ? (
            <div
              className="flex flex-col gap-2 rounded-2xl border p-5 text-sm"
              style={{
                background: 'var(--bg-surface)',
                borderColor: 'var(--border)',
                color: 'var(--text-muted)',
              }}
              role="alert"
              data-testid="body-error"
            >
              <span>Failed to load measurements: {metrics.error}</span>
              <button
                type="button"
                onClick={() => void metrics.refresh()}
                className="self-start rounded-md border px-3 py-2 text-xs font-medium"
                style={{
                  background: 'transparent',
                  borderColor: 'var(--border)',
                  color: 'var(--text-main)',
                  minHeight: 48,
                }}
                data-testid="body-retry"
              >
                Retry
              </button>
            </div>
          ) : (
            <MetricsTable
              entries={metrics.entries}
              onEdit={onEdit}
              onDelete={onAskDelete}
            />
          )}
        </>
      )}

      <MetricEntryDialog
        open={dialogState !== null}
        mode={dialogState?.mode ?? 'create'}
        initial={dialogState?.mode === 'edit' ? dialogState.entry : null}
        onClose={() => setDialogState(null)}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />

      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        target={deleteTarget}
        busy={deleting}
        onCancel={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        onConfirm={() => void onConfirmDelete()}
      />
    </section>
  );
}
